import { defineStore } from 'pinia'
import type { RouteRecordRaw } from 'vue-router'
/**
 * 此处持久化使用MapCache用于确保数据安全性
 */
import { MapCache } from '@fanghub/tool-cache'
import type { PluginCache } from '@fanghub/tool-cache'
// 请求动态路由接口
import { getRoutersTree } from '@/http/apis/index'
import type { Router } from '@/http/apis/index'
import { staticPageRoutes } from '@/router'

const mapCache: PluginCache<string | null> = new MapCache<string | null>()
// 匹配views里面所有的.vue文件
const modules = import.meta.glob('@/views/**/*.vue')

// permission 持久化接口
export interface PermissionState {
    // 所有路由列表
    allRoutes: Array<RouteRecordRaw>
    // 动态路由列表
    dynamicsRoutes: Array<RouteRecordRaw>
    // 静态路由列表
    staticRoutes: Array<RouteRecordRaw>
    // 侧边栏路由列表
    sidebarRouters: Array<RouteRecordRaw>
    // 选中的路由
    activeRoute: RouteRecordRaw | null
    // 选中一级路由
    activeLevel1Route: RouteRecordRaw | null
    // 选中的二级路由
    activeLevel2Route: RouteRecordRaw | null
    // 按钮权限列表
    buttonPermissionList: Array<string>
}

/**
 * 导航权限相关store
 */
const usePermissionStore = defineStore('permission', {
    state: (): PermissionState => ({
        // 所有路由列表
        allRoutes: [],
        // 动态路由列表
        dynamicsRoutes: [],
        // 静态路由列表
        staticRoutes: staticPageRoutes,
        // 侧边栏路由列表
        sidebarRouters: [],
        // 当前选中的菜单
        activeRoute: null,
        // 选中的一级路由
        activeLevel1Route: null,
        // 选中的二级路由
        activeLevel2Route: null,
        // 按钮权限列表
        buttonPermissionList: []
    }),
    /**
     * 持久化，这里采用分字段单独持久化，方便读取
     * sessionCache 为自定义持久化方法，此方法添加了加密算法。使得持久化的内容都是经过加密处理的
     */
    persist: {
        key: 'permission',
        storage: mapCache, // 修改此处
        pick: [
            'allRoutes',
            'dynamicsRoutes',
            'staticRoutes',
            'sidebarRouters',
            'activeRoute',
            'activeLevel1Route',
            'activeLevel2Route',
            'buttonPermissionList'
        ]
    },
    actions: {
        /**
         * 修改当前state的值
         * @param {string} key 缓存key
         * @param {string} value 缓存value
         * @returns {void}
         */
        setStore<T>({ key, value }: { key: string; value: T }) {
            if (key) {
                this[key] = value
            }
            return null
        },
        /**
         * 生成动态路由
         */
        async generateRoutes(): Promise<RouteRecordRaw[]> {
            try {
                const res = await getRoutersTree({ hasButton: true })
                const routes = res.data.map((item: Router) => buildRouteTree(item))
                // 动态路由
                this.dynamicsRoutes = filterDynamicRoutes(routes)
                // 所有路由（动态 + 静态）
                this.allRoutes = [...staticPageRoutes, ...routes]
                // 侧边导航路由（过滤重定向和设置了隐藏的路由）
                this.sidebarRouters = filterSliderBarRoutes(this.allRoutes)
                // 所有按钮权限
                this.buttonPermissionList = findButtonPermission(routes)
                return this.dynamicsRoutes
            } catch (error) {
                console.error('获取动态路由失败:', error)
                // 可以选择返回一个默认路由数组，而不是抛出错误
                // 这样可以让程序继续执行，而不是阻塞
                // 例如，可以只返回静态路由作为后备
                this.dynamicsRoutes = [] // 或者使用默认路由
                this.allRoutes = staticPageRoutes
                this.sidebarRouters = filterSliderBarRoutes(staticPageRoutes)
                this.buttonPermissionList = []
                return this.dynamicsRoutes // 返回空数组或默认路由
            }
        }
    },
    getters: {
        /**
         * 获取当前state的值
         * @param {string} key 缓存key
         * @return {string} 缓存value
         */
        getStore: (state: PermissionState) => (key: string) => {
            if (key) {
                return state[key]
            }
            return null
        }
    }
})

/**
 *
 * @param routeItem 路由项
 * @param pathRoot 配置Layout布局组件的path
 * @returns
 */
const buildRouteTree = (routeItem: Router, pathRoot: string = '/'): CustomRouteRecordRaw => {
    // 加载页面文件
    const component = resolveComponent(routeItem.component)
    // 将路由名称转为小写
    let lowerCaseName = routeItem.name.toLowerCase()
    // 去掉路由名称开头的斜杠
    if (lowerCaseName.startsWith('/')) {
        lowerCaseName = lowerCaseName.substring(1)
    }
    // 获取当前路由项的path
    let path = routeItem.path
    /**
     * 正常配置菜单或者目录时，省略path属性。由此处动态拼接path地址
     * 拼接的规则为累加所有父级的name，并追加当前路由的name
     * 例如：当前路由的name为user，父级的name为admin，父级的父级name为system。 则此处拼接后的path为 /system/admin/user
     * 当配置了path属性，则此处path值使用配置的path 值
     */
    if (!path) {
        path = pathRoot + lowerCaseName
    }
    // 构建路由对象
    const route = {
        ...routeItem,
        path,
        component,
        meta: {
            ...routeItem.meta
        }
    }

    // 处理路由重定向，默认以/开头的为绝对路径不做二次修改，
    // 不以/开头的路径为相对路径，需要添加当前路由路径作为绝对路径执行页面重定向
    if (routeItem.redirect && !routeItem.redirect.startsWith('/')) {
        route.redirect = `${path}/${routeItem.redirect}`
    }

    // 递归处理子路由
    if (routeItem.children) {
        route.children = routeItem.children.map(childRouteItem => buildRouteTree(childRouteItem, `${path}/`))
    }
    return route
}
/**
 * @description 解析组件路径
 * @param componentPath
 * @returns
 */
const resolveComponent = (componentPath: string): (() => Promise<unknown>) => {
    // TODO 路径标准化 // /src/views/xxx/yyy.vue
    let cPath = componentPath
        // 替换别名, @views
        .replace('@', '')
    // 处理跟路径
    if (!cPath.startsWith('/')) {
        cPath = `/${cPath}`
    }
    // 处理文件后缀
    if (!cPath.endsWith('.vue')) {
        cPath = `${cPath}.vue`
    }
    // 处理全路径
    cPath = `/src${cPath}`
    return modules[cPath]
}
/**
 * @description 过滤动态路由数据
 * @param routes
 * @returns
 */
const filterDynamicRoutes = (routes: RouteRecordRaw[]): RouteRecordRaw[] => {
    return routes.reduce<RouteRecordRaw[]>((acc, route) => {
        // 如果有outLink属性，则过滤掉该数据（不添加到路由中）
        if (route.meta?.outLink) {
            return acc
        }
        // 深拷贝当前路由对象，避免修改原始数据
        const clonedRoute: RouteRecordRaw = {
            ...route
        }

        // 处理子路由
        if (clonedRoute.children) {
            clonedRoute.children = filterDynamicRoutes(clonedRoute.children)
        }
        // 无页面的目录默认打开其下第一个可访问页面，支持多级目录。
        if (!clonedRoute.component && !clonedRoute.redirect) {
            const firstPage = clonedRoute.children?.find(child =>
                !child.meta?.hidden && !child.meta?.disabled && (child.component || child.redirect)
            )
            if (firstPage) {
                clonedRoute.redirect = firstPage.path
            }
        }
        acc.push(clonedRoute)
        return acc
    }, [])
}
/**
 * @description 过滤侧边栏路由
 * @param routes
 * @returns 侧边栏路由
 */
const filterSliderBarRoutes = (routes: RouteRecordRaw[]): RouteRecordRaw[] => {
    // 深拷贝路由数据避免污染原始数据
    const deepCloneRoutes: RouteRecordRaw[] = JSON.parse(JSON.stringify(routes))
    // 递归处理函数
    const processRoutes = (routeList: RouteRecordRaw[]): RouteRecordRaw[] => {
        return routeList.reduce<RouteRecordRaw[]>((acc, route) => {
            // 如果路由未被隐藏且不是通配符路由
            if (!route.meta?.hidden && route.path !== '/:pathMatch(.*)*') {
                // 保留当前路由并递归处理子路由
                const processedRoute = {
                    ...route,
                    children: route.children ? processRoutes(route.children) : undefined
                } as RouteRecordRaw
                acc.push(processedRoute)
            } else if (route.children && route.path !== '/:pathMatch(.*)*' && route.path !== '/redirect') {
                // 如果路由被隐藏但存在子路由，将子路由提升到当前层级
                acc.push(...processRoutes(route.children))
            }
            return acc
        }, [])
    }
    return processRoutes(deepCloneRoutes)
}
/**
 * @description 获取页面权限
 * @returns
 */
const findButtonPermission = (routes: RouteRecordRaw[]): string[] => {
    // 深拷贝路由数据避免污染原始数据
    const deepCloneRoutes: RouteRecordRaw[] = JSON.parse(JSON.stringify(routes))
    const permissionList: string[] = []
    // 递归处理函数
    const processRoutes = (routeList: RouteRecordRaw[]) => {
        routeList.forEach(route => {
            if (route.meta && route.meta.buttons) {
                route.meta.buttons.forEach(item => {
                    if (item.code) {
                        permissionList.push(item.code)
                    }
                })
            }
            if (route.children) {
                processRoutes(route.children)
            }
        })
    }
    return (processRoutes(deepCloneRoutes), permissionList)
}
export default usePermissionStore
